<script>
  import { link } from 'svelte-spa-router';
  import { t } from '../i18n/index.js';
  import { companyInvoicesApi } from '../stores/api.js';
  import { activeCompanyId } from '../stores/company.js';
  import { canWriteInCompany } from '../stores/capabilities.js';
  import { unreadInbound } from '../stores/unread.js';
  import { badgeText } from '../lib/read-state.js';

  /**
   * On /invoices, Tutte, Inviate and Ricevute are views of the same list, so
   * they are a real tab set (arrow keys move between them, the panel below
   * follows). Bozze is a different page, so it is a link next to them that
   * says `aria-current="page"` when you are on it.
   * On /drafts all three are links.
   *
   * `active` is 'all', 'outbound', 'inbound' or 'drafts'; `count` is the
   * number of rows the page is showing for the active one.
   */
  export let active = 'outbound';
  export let count = null;
  /** Called with 'all' | 'outbound' | 'inbound' when a tab is chosen on /invoices. */
  export let onSelect = null;
  /** The id of the tab panel the tabs control (on /invoices). */
  export let panelId = 'invoices-panel';

  const DIRECTIONS = ['all', 'outbound', 'inbound'];
  const LINKS = ['outbound', 'inbound'];
  const LABEL = { all: 'invoices.tab.all', outbound: 'invoices.tab.sent', inbound: 'invoices.tab.received' };

  let draftCount = null;
  async function loadDraftCount(companyId, allowed) {
    if (!companyId || !allowed) { draftCount = null; return; }
    try {
      const drafts = await companyInvoicesApi.listDrafts(companyId);
      draftCount = Array.isArray(drafts) ? drafts.length : null;
    } catch {
      draftCount = null;
    }
  }
  $: loadDraftCount($activeCompanyId, $canWriteInCompany);
  $: if (active === 'drafts' && count != null) draftCount = count;

  function onKeydown(event) {
    const index = DIRECTIONS.indexOf(active);
    let next = null;
    if (event.key === 'ArrowRight') next = DIRECTIONS[(index + 1) % DIRECTIONS.length];
    else if (event.key === 'ArrowLeft') next = DIRECTIONS[(index - 1 + DIRECTIONS.length) % DIRECTIONS.length];
    else if (event.key === 'Home') next = DIRECTIONS[0];
    else if (event.key === 'End') next = DIRECTIONS[DIRECTIONS.length - 1];
    if (!next) return;
    event.preventDefault();
    onSelect?.(next);
    document.getElementById(`invoices-tab-${next}`)?.focus();
  }
</script>

<div class="tab-bar">
  {#if onSelect}
    <div class="tab-list" role="tablist" aria-label={$t('invoices.tab.groupLabel')}>
      {#each DIRECTIONS as direction}
        <button type="button" role="tab" id="invoices-tab-{direction}" class="tab-button"
          aria-selected={active === direction} aria-controls={panelId}
          tabindex={active === direction ? 0 : -1}
          on:click={() => onSelect(direction)} on:keydown={onKeydown}>
          <span>{$t(LABEL[direction])}</span>
          {#if active === direction && count != null}
            <span class="tab-count"><span class="sr-only">, </span>{count}</span>
          {/if}
          {#if direction === 'inbound' && $unreadInbound > 0}
            <span class="tab-count-alert" title={$t('invoices.tab.unreadTooltip', { n: $unreadInbound })}
              aria-label={$t('invoices.tab.unreadTooltip', { n: $unreadInbound })}>{badgeText($unreadInbound)}</span>
          {/if}
        </button>
      {/each}
    </div>
  {/if}

  <nav class="tab-list" aria-label={$t('invoices.tab.pagesLabel')}>
    {#if !onSelect}
      {#each LINKS as direction}
        <a href="/invoices?direction={direction}" use:link class="tab-button">
          <span>{$t(LABEL[direction])}</span>
          {#if direction === 'inbound' && $unreadInbound > 0}
            <span class="tab-count-alert" title={$t('invoices.tab.unreadTooltip', { n: $unreadInbound })}
              aria-label={$t('invoices.tab.unreadTooltip', { n: $unreadInbound })}>{badgeText($unreadInbound)}</span>
          {/if}
        </a>
      {/each}
    {/if}
    {#if $canWriteInCompany}
      <a href="/drafts" use:link class="tab-button" aria-current={active === 'drafts' ? 'page' : undefined}
        title={$t('invoices.tab.draftsTooltip')}>
        <span>{$t('drafts.title')}</span>
        {#if draftCount != null}
          <span class="tab-count"><span class="sr-only">, </span>{draftCount}</span>
        {/if}
      </a>
    {/if}
  </nav>
</div>
